import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { createRequestGate } from '../dashboards/requestGate';
import { supabase } from '../supabase';
import { User } from '@supabase/supabase-js';
import { formatCurrency } from '../utils';
import { resolveContactInfo } from '../utils/contactFallback';
import { assigneeScopeCondition } from '../utils/salesActivityCreatorFilter';
import { useSettingsStore } from './settingsStore';
import { MONTH_SHORT_IT, aggregateServiceTrends, trendWindow } from '../dashboards/serviceTrends';
import { aggregateTopServices } from '../dashboards/topServices';
import { fetchAllPages } from '../dashboards/fetchAllPages';
import { scopeIds, inScope, applyScope } from '../dashboards/personScope';
import { rememberServicesScope } from '../dashboards/detail/servicesDetail';
import { resolveServicesRange } from '../dashboards/detail/servicesDetailRows';

// ── FIC invoice helpers ──────────────────────────────────────────────
interface FicInvoiceRow {
  net_amount: number | null;
  type: string;
  date: string;
  deal_id: string | null;
  deal_service_id: string | null;
  deals: { assigned_to: string | null } | null;
  deal_services: {
    service_id: string;
    services: {
      category_id: string | null;
      service_categories: { id: string; name: string; color: string | null } | null;
    } | null;
  } | null;
}

/** True when the org has opted to use FIC invoices AND FIC is actually connected. */
const shouldUseFicRevenue = async (organizationId: string): Promise<boolean> => {
  const settings = useSettingsStore.getState().settings;
  if (settings?.dashboard_revenue_source !== 'invoiced') return false;

  const { data } = await supabase
    .from('fattureincloud_configs')
    .select('is_connected')
    .eq('organization_id', organizationId)
    .maybeSingle();

  return data?.is_connected === true;
};

/** Fetch FIC invoices for a broad date range (single query with embedded joins). */
const fetchFicInvoices = async (
  organizationId: string,
  startDate: string,
  endDate: string
): Promise<FicInvoiceRow[]> => {
  // A pagine (PostgREST taglia a 1.000 righe) e con ordine stabile per id
  const { data, error } = await fetchAllPages((from, to) => supabase
    .from('fattureincloud_invoices')
    .select(`
      net_amount, type, date, deal_id, deal_service_id,
      deals(assigned_to),
      deal_services(service_id, services(category_id, service_categories(id, name, color)))
    `)
    .eq('organization_id', organizationId)
    .in('type', ['invoice', 'credit_note'])
    .neq('status', 'cancelled')
    .gte('date', startDate)
    .lt('date', endDate)
    .order('id')
    .range(from, to));

  if (error) {
    console.error('[Dashboard] FIC invoices query error:', error);
    return [];
  }
  return (data || []) as unknown as FicInvoiceRow[];
};

/**
 * Filtro persona sulle fatture: la fattura e' della persona a cui e' assegnata l'offerta collegata
 * (fattureincloud_invoices.deal_id -> deals.assigned_to). Le fatture senza offerta collegata, o con offerta senza
 * assegnato, non si possono attribuire a nessuno: con un filtro persona attivo vengono ESCLUSE (senza filtro restano).
 */
const filterFicByUsers = (rows: FicInvoiceRow[], ids: readonly string[]): FicInvoiceRow[] =>
  ids.length === 0 ? rows : rows.filter(inv => inScope(ids, inv.deals?.assigned_to));

/** Calculate net revenue from a list of FIC invoice rows (invoices - credit notes). */
const calcFicRevenue = (rows: FicInvoiceRow[]): number =>
  rows.reduce((sum, inv) => {
    const amt = Number(inv.net_amount || 0);
    return sum + (inv.type === 'credit_note' ? -amt : amt);
  }, 0);

// Utility functions for date calculations
const getMonthRange = (month: number, year: number) => {
  // Convert month to 1-based index if needed (0-11 to 1-12)
  const normalizedMonth = month > 0 && month <= 12 ? month : new Date().getMonth() + 1;
  const normalizedYear = year || new Date().getFullYear();
  
  // Calculate month start
  const monthStart = `${normalizedYear}-${normalizedMonth.toString().padStart(2, '0')}-01`;
  
  // Calculate next month for the range end
  const nextMonth = normalizedMonth === 12 ? 1 : normalizedMonth + 1;
  const nextMonthYear = normalizedMonth === 12 ? normalizedYear + 1 : normalizedYear;
  const monthEnd = `${nextMonthYear}-${nextMonth.toString().padStart(2, '0')}-01`;
  
  return { monthStart, monthEnd, month: normalizedMonth, year: normalizedYear };
};

// Generate month names for display
// Nomi brevi dei mesi in italiano (gen, feb, ...): le etichette dei grafici non devono essere in inglese
const MONTH_NAMES = MONTH_SHORT_IT;

// Voce delle categorie per i servizi che non ne hanno una
const UNCATEGORIZED = { id: '__uncategorized__', name: 'Senza categoria', color: null as string | null };
const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export interface SalesTarget {
  id: string;
  user_id: string;
  organization_id: string;
  target_amount: number;
  month: number;
  year: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    full_name?: string;
  };
}

export interface SalesPerformer {
  user_id: string;
  user_name: string;
  total_revenue: number;
  total_deals: number;
  target_amount: number;
  achievement_percentage: number;
  avg_deal_size: number;
  conversion_rate: number;
  highest_deal: number;
  // Additional fields
  avatar?: string | null;
  rank?: number;
}

export interface SalesFunnel {
  stage: string;
  count: number;
  value: number;
  conversion_rate: number;
  previous_stage?: string;
}

// Activity interface
export interface SalesActivity {
  id: string;
  title: string;
  description?: string;
  type: 'call' | 'meeting' | 'email' | 'task' | 'other';
  status: 'pending' | 'completed' | 'overdue';
  due_date: string;
  client_id?: string;
  client_name?: string;
  clients?: {
    business_name: string;
  };
  lead_id?: string;
  deal_id?: string;
  deal_title?: string;
  deal_status?: string;
  deal_amount?: number;
  user_id: string;
  assigned_to?: string;
  assignee?: {
    id: string;
    full_name: string;
    email: string;
  };
  /** Chi ha creato la task (user_id) */
  creator?: {
    id: string;
    full_name: string;
    email: string;
  };
  created_at?: string;
  completed_at?: string;
  outcome?: string;
  end_date?: string | null;
  google_event_id?: string | null;
  calendar_id?: string | null;
  microsoft_event_id?: string | null;
  meet_link?: string | null;
  metadata?: Record<string, any> | null;
  deal_contacts?: Array<{
    id: string;
    contact?: {
      id: string;
      name: string;
      phone?: string;
      email?: string;
      role?: string;
    };
  }>;
  deal_services?: Array<{
    service_id: string;
    service?: {
      id: string;
      title: string;
    };
  }>;
  contact_info?: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    role?: string;
    source: 'deal' | 'client';
  };
  /** stato_cliente della riga clients collegata ('lead' | 'cliente'), per l'etichetta Lead/Cliente */
  client_status?: string | null;
}

// Team performance over time
export interface TeamPerformance {
  month: string;
  deals_closed: number;
  deals_created: number;
  conversion_rate: number;
  revenue: number;
  month_number: number;
  year: number;
}

// Sales Analytics Interfaces
export interface ServiceCategoryAnalysis {
  categoryId: string;
  categoryName: string;
  categoryColor: string | null;
  totalRevenue: number;
  dealsCount: number;
  servicesCount: number;
  percentageOfTotal: number;
}

export interface UserCategoryPerformance {
  userId: string;
  userName: string;
  categories: {
    [categoryId: string]: {
      categoryName: string;
      categoryColor: string | null;
      revenue: number;
      dealsCount: number;
      percentageOfUserTotal: number;
    };
  };
  topCategory: {
    categoryId: string;
    categoryName: string;
    revenue: number;
    specializationRate: number; // % del totale utente
  } | null;
}

export interface ServiceTypeRevenueTrend {
  month: string;
  monthNumber: number;
  year: number;
  oneTimeRevenue: number;
  recurringRevenue: number;
  totalRevenue: number;
  mrr: number; // Monthly Recurring Revenue
}

export interface TopServicePerformance {
  serviceId: string;
  serviceName: string;
  categoryName: string | null;
  categoryColor: string | null;
  serviceType: string;
  totalRevenue: number;
  dealsCount: number;
  averagePrice: number;
  averageDiscountRate: number;
  /** Sconto e listino sommati (servono per lo sconto medio pesato) */
  totalDiscount: number;
  totalAmount: number;
  rank: number;
  /** Riga senza servizio collegato (prodotto, nome personalizzato) */
  isCustom?: boolean;
}

// Store interface
interface SalesTargetStore {
  // Data
  targets: SalesTarget[];
  performers: SalesPerformer[];
  performersPodiumData: SalesPerformer[]; // Separate data for PerformersPodium component
  funnel: SalesFunnel[];
  activities: SalesActivity[];
  teamPerformance: TeamPerformance[];

  // Sales Analytics Data
  serviceCategoriesAnalysis: ServiceCategoryAnalysis[];
  userCategoryPerformance: UserCategoryPerformance[];
  serviceTypeTrends: ServiceTypeRevenueTrend[];
  topServicesPerformance: TopServicePerformance[];

  // Stats
  monthlyRevenue: number;
  conversionRate: number;
  conversionRateMonthly: number; // Deals creati e chiusi nello stesso mese
  conversionRateYearly: number; // Deals creati e chiusi nello stesso anno
  avgDealSize: number;
  totalActivities: number;
  
  // Organization data cache
  cachedOrganizationId: string | null;
  cachedUserId: string | null;
  
  // User filter
  selectedUserId: string | null;
  setSelectedUser: (userId: string | null) => void;
  /** Due o piu' persone scelte nella barra (con 0 o 1 persona resta vuoto e vale selectedUserId). */
  selectedUserIds: string[];
  setSelectedUsers: (userIds: string[]) => void;

  // Current dashboard month/year
  currentDashboardMonth: number;
  /** Estremi del periodo letto (inizio incluso, fine esclusa, 'YYYY-MM-DD'): per il dettaglio dei numeri, stessi criteri dei calcoli. */
  dashboardPeriodStart: string | null;
  dashboardPeriodEnd: string | null;
  currentDashboardYear: number;

  // Performers period filter
  performersPeriodType: 'monthly' | 'yearly';
  setPerformersPeriodType: (period: 'monthly' | 'yearly') => void;
  
  // Constants
  MONTH_NAMES: string[];
  MONTH_NAMES_FULL: string[];
  
  // State
  loading: boolean;
  statsLoading: boolean;
  performersLoading: boolean;
  performersPodiumLoading: boolean;
  funnelLoading: boolean;
  activitiesLoading: boolean;
  teamLoading: boolean;
  error: string | null;

  // Analytics Loading States
  categoriesAnalysisLoading: boolean;
  userCategoryLoading: boolean;
  serviceTypeTrendsLoading: boolean;
  topServicesLoading: boolean;
  
  // Organization ID management
  getOrganizationId: (user: User) => Promise<string>;
  
  // Actions for targets management
  fetchTargets: (month?: number, year?: number) => Promise<void>;
  createTarget: (target: any) => Promise<boolean>;
  updateTarget: (id: string, target: any) => Promise<boolean>;
  deleteTarget: (id: string) => Promise<boolean>;
  
  // Batch data loading
  fetchDashboardData: (user: User | null, month?: number, year?: number, filterUserId?: string | null, dateRange?: { from: string; to: string } | null) => Promise<any>;
  
  // Individual data loading actions
  fetchDashboardStats: (user: User | null, month?: number, year?: number, filterUserId?: string | null) => Promise<void>;
  fetchPerformers: (user: User | null, month?: number, year?: number, filterUserId?: string | null) => Promise<void>;
  fetchPerformersOnly: (user: User | null, filterUserId?: string | null) => Promise<void>;
  fetchSalesFunnel: (user: User | null, month?: number, year?: number, filterUserId?: string | null) => Promise<void>;
  /** options.silent: aggiorna i dati senza passare per lo stato loading
   *  (la lista resta visibile, refresh in background dopo un salvataggio). */
  fetchActivities: (user: User | null, filterUserId?: string | null, options?: { silent?: boolean; createdBy?: string | null; includeUnassignedCreated?: boolean }) => Promise<void>;
  fetchTeamPerformance: (user: User | null, months?: number, filterUserId?: string | null) => Promise<void>;

  // Sales Analytics Actions
  fetchServiceCategoriesAnalysis: (user: User | null, month?: number, year?: number, filterUserId?: string | null, dateRange?: { from: string; to: string } | null) => Promise<void>;
  fetchUserCategoryPerformance: (user: User | null, month?: number, year?: number, filterUserId?: string | null, dateRange?: { from: string; to: string } | null) => Promise<void>;
  fetchServiceTypeTrends: (user: User | null, months?: number, filterUserId?: string | null) => Promise<void>;
  fetchTopServicesPerformance: (user: User | null, month?: number, year?: number, limit?: number, filterUserId?: string | null, dateRange?: { from: string; to: string } | null) => Promise<void>;
  fetchSalesAnalytics: (user: User | null, month?: number, year?: number, filterUserId?: string | null, dateRange?: { from: string; to: string } | null) => Promise<void>;

  // Reset
  reset: () => void;
}

// Cache time in milliseconds (5 minutes)
const CACHE_TIME = 5 * 60 * 1000;

/** Ultima richiesta della Dashboard e dei grafici dei servizi: le risposte superate non scrivono nello store. */
const dashboardGate = createRequestGate();
const analyticsGates: Record<string, ReturnType<typeof createRequestGate>> = {};
const analyticsGate = {
  begin: (name: string) => ({ name, token: (analyticsGates[name] ??= createRequestGate()).begin() }),
  guard: <A extends unknown[]>(t: { name: string; token: number }, set: (...args: A) => void) => analyticsGates[t.name].guard(t.token, set),
};

export const useSalesTargetStore = create<SalesTargetStore>((set, get) => ({
  // Data
  targets: [],
  performers: [],
  funnel: [],
  activities: [],
  teamPerformance: [],
  performersPodiumData: [],

  // Sales Analytics Data
  serviceCategoriesAnalysis: [],
  userCategoryPerformance: [],
  serviceTypeTrends: [],
  topServicesPerformance: [],

  // Stats
  monthlyRevenue: 0,
  conversionRate: 0,
  conversionRateMonthly: 0,
  conversionRateYearly: 0,
  avgDealSize: 0,
  totalActivities: 0,

  // Organization data cache
  cachedOrganizationId: null,
  cachedUserId: null,

  // User filter
  selectedUserId: null,
  setSelectedUser: (userId) => set({ selectedUserId: userId }),
  selectedUserIds: [],
  setSelectedUsers: (userIds) => set({ selectedUserIds: userIds }),

  // Current dashboard month/year (initialize with current date)
  currentDashboardMonth: new Date().getMonth() + 1,
  dashboardPeriodStart: null,
  dashboardPeriodEnd: null,
  currentDashboardYear: new Date().getFullYear(),

  // Performers period filter
  performersPeriodType: 'monthly',
  setPerformersPeriodType: async (period) => {
    set({ performersPeriodType: period });
    // Trigger performers data refresh only (not entire dashboard)
    const { data: userData } = await supabase.auth.getUser();
    if (userData.user) {
      const state = get();
      await state.fetchPerformersOnly(userData.user, state.selectedUserId);
    }
  },
  
  // Constants
  MONTH_NAMES: MONTH_NAMES,
  MONTH_NAMES_FULL: MONTH_NAMES_FULL,
  
  // State
  loading: false,
  statsLoading: false,
  performersLoading: false,
  performersPodiumLoading: false,
  funnelLoading: false,
  activitiesLoading: false,
  teamLoading: false,
  error: null,

  // Analytics Loading States
  categoriesAnalysisLoading: false,
  userCategoryLoading: false,
  serviceTypeTrendsLoading: false,
  topServicesLoading: false,
  
  // Organization ID Management
  getOrganizationId: async (user) => {
    // If we have a cached organization ID for this user, return it
    if (get().cachedOrganizationId && get().cachedUserId === user.id) {
      return get().cachedOrganizationId;
    }
    
    // Otherwise, fetch it from the database
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();
    
    if (error || !profile?.organization_id) {
      throw new Error('User not associated with an organization');
    }
    
    // Cache the organization ID for future use
    set({ 
      cachedOrganizationId: profile.organization_id,
      cachedUserId: user.id
    });
    
    return profile.organization_id;
  },
  
  // Batch load all dashboard data at once
  fetchDashboardData: async (user, month, year, filterUserId = null, dateRange = null) => {
    if (!user) return;
    // Vale solo l'ultima richiesta: cambiando filtro due volte di fila la risposta del primo non deve vincere
    const token = dashboardGate.begin();
    const isLatest = () => dashboardGate.isCurrent(token);
    const setIf = dashboardGate.guard(token, set);

    set({
      statsLoading: true,
      performersLoading: true,
      funnelLoading: true,
      activitiesLoading: true,
      teamLoading: true,
      error: null
    });

    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);
      // Persone su cui filtrare: una (filterUserId), piu' d'una (barra) o nessuna = tutto il team
      const scope = scopeIds(filterUserId, get().selectedUserIds);

      // Use custom date range if provided, otherwise use month range
      let monthStart: string;
      let monthEnd: string;
      let currentMonth: number;
      let currentYear: number;

      if (dateRange && dateRange.from && dateRange.to) {
        // Custom date range: use the provided dates
        monthStart = dateRange.from;
        // Add 1 day to dateTo to make it inclusive (since we use < comparison)
        const toDate = new Date(dateRange.to);
        toDate.setDate(toDate.getDate() + 1);
        monthEnd = toDate.toISOString().split('T')[0];
        // Extract month/year from start date for display purposes
        const fromDate = new Date(dateRange.from);
        currentMonth = fromDate.getMonth() + 1;
        currentYear = fromDate.getFullYear();
      } else {
        // Use default month range
        const range = getMonthRange(month || 0, year || 0);
        monthStart = range.monthStart;
        monthEnd = range.monthEnd;
        currentMonth = range.month;
        currentYear = range.year;
      }

      // Update store with current dashboard month/year
      setIf({
        currentDashboardMonth: currentMonth,
        currentDashboardYear: currentYear,
        dashboardPeriodStart: monthStart,
        dashboardPeriodEnd: monthEnd,
      });

      // Prepare data loading for month/year range
      
      // Create two queries: one for filtered deals (stats/activities) and one for ALL deals (performers/team)
      // Query filtered deals for stats and activities
      // Le offerte si leggono a pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id
      const dealsPage = (from: number, to: number, onlyScope: boolean) => {
        const q = supabase
          .from('deals')
          .select(`
            id,
            amount,
            status,
            created_at,
            signed_at,
            assigned_to,
            user_profiles!deals_assigned_to_fkey (
              id, email, full_name, avatar_url
            ),
            deal_services(id, amount, monthly_duration, discounted_amount)
          `)
          .eq('organization_id', organizationId)
          .order('id')
          .range(from, to);
        // Filtro persona (una o piu') solo per le offerte di statistiche e funnel
        return onlyScope ? applyScope(q, 'assigned_to', scope) : q;
      };
      const filteredDealsQuery = fetchAllPages((from, to) => dealsPage(from, to, true));

      // Query ALL deals for performers and team performance (no user filter)
      const allDealsQuery = fetchAllPages((from, to) => dealsPage(from, to, false));
      
      // Create the base activities query
      let activitiesQuery = supabase
        .from('sales_activities')
        .select(`
          id,
          title,
          description,
          type,
          status,
          due_date,
          created_at,
          client_id,
          clients:client_id (id, business_name, entity_type, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
          lead_id,
          leads:lead_id (id, business_name, entity_type, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
          partner_id,
          partners:external_partners!sales_activities_partner_id_fkey(id, name),
          deal_id,
          deals:deal_id (
            id,
            title,
            status,
            amount,
            client_id,
            clients:client_id (business_name, entity_type, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
            deal_contacts (
              id,
              contact:client_contacts (
                id,
                name,
                phone,
                email,
                role
              )
            )
          ),
          user_id,
          assigned_to,
          assignee:assigned_to (
            id,
            full_name
          ),
          completed_at,
          outcome
        `)
        .eq('organization_id', organizationId)
        // Escludi le completate oltre 90gg: senza questo filtro PostgREST tronca a 1000 righe
        // e i task futuri (in coda per due_date asc) spariscono dalla dashboard
        .or(`status.neq.completed,completed_at.is.null,completed_at.gte.${new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()}`)
        .order('due_date', { ascending: true });

      // Apply filter by user for activities if provided
      // filterUserId = null means "all users", filterUserId = specific ID means filter to that user
      if (scope.length > 0) {
        // Filter to specific user(s) only
        activitiesQuery = applyScope(activitiesQuery, 'assigned_to', scope);
      }
      // Nessuna persona scelta: don't apply any user filter - show all activities
      
      // Create parallel promises for all data fetching operations
      const promises: PromiseLike<any>[] = [
        // 1. Fetch filtered deals for stats and funnel
        filteredDealsQuery,

        // 2. Fetch ALL deals (no filter) for performers and team performance
        allDealsQuery,

        // 3. Fetch user profiles for the organization (for team performance)
        supabase
          .from('user_profiles')
          .select('id, email, full_name, avatar_url')
          .eq('organization_id', organizationId),

        // 4. Fetch sales targets for the current month
        supabase
          .from('sales_targets')
          .select('user_id, target_amount')
          .eq('organization_id', organizationId)
          .eq('month', currentMonth)
          .eq('year', currentYear),

        // 5. Fetch activities with filters applied
        activitiesQuery
      ];

      // Execute all promises in parallel
      const [
        { data: filteredDeals, error: filteredDealsError },
        { data: allDealsForPerformers, error: allDealsError },
        { data: team, error: teamError },
        { data: targets, error: targetsError },
        { data: activitiesData, error: activitiesError }
      ] = await Promise.all(promises);

      if (filteredDealsError) throw filteredDealsError;
      if (allDealsError) throw allDealsError;
      if (teamError) throw teamError;
      if (activitiesError) {
        console.error('[Dashboard] Activities query error:', activitiesError);
        throw activitiesError;
      }

      // Filter deals for current month (for stats and funnel) - using FILTERED deals
      const monthDeals = filteredDeals?.filter(d => {
        // Per i contratti accettati usa SEMPRE signed_at
        if (d.status === 'accepted') {
          return d.signed_at && d.signed_at >= monthStart && d.signed_at < monthEnd;
        }
        // Per draft/sent/rejected usa created_at (per il funnel delle trattative)
        return d.created_at && d.created_at >= monthStart && d.created_at < monthEnd;
      }) || [];

      // `performers` (obiettivo in Statistiche, Obiettivo di vendita, "Quanto ha incassato ognuno" vista Mese) e'
      // SEMPRE sul periodo scelto; la classifica Mese/Anno vive a parte in performersPodiumData (fetchPerformersOnly)
      const performersPeriod = get().performersPeriodType;
      const performersStartDate = monthStart;
      const performersEndDate = monthEnd;
      // Previous month
      const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
      const prevMonthRange = getMonthRange(prevMonth, prevYear);
      const previousStartDate = prevMonthRange.monthStart;
      const previousEndDate = prevMonthRange.monthEnd;

      const allMonthDeals = allDealsForPerformers?.filter(d => {
        const dealDate = d.signed_at;
        return dealDate && dealDate >= performersStartDate && dealDate < performersEndDate;
      }) || [];

      // Filter previous period deals for performance comparison
      const previousPeriodDeals = allDealsForPerformers?.filter(d => {
        const dealDate = d.signed_at;
        return dealDate && dealDate >= previousStartDate && dealDate < previousEndDate;
      }) || [];

      // ── FIC revenue override check ──
      const useFic = await shouldUseFicRevenue(organizationId);
      let ficInvoices: FicInvoiceRow[] = [];
      if (useFic) {
        // Broad range: 6 months ago → end of current year (covers teamPerformance + performers yearly)
        // Da gennaio (o 7 mesi fa, se prima): servono tutti i mesi dell'anno per i Ricavi annuali
        const ficStart = new Date(Math.min(new Date(currentYear, 0, 1).getTime(), new Date(currentYear, currentMonth - 7, 1).getTime())).toISOString().split('T')[0];
        const ficEnd = `${currentYear + 1}-01-01`;
        ficInvoices = await fetchFicInvoices(organizationId, ficStart, ficEnd);
      }

      // Filter completed for current month

      // Process dashboard stats
      const closedDeals = monthDeals?.filter(d => d.status === 'accepted') || [];

      // R18a (richiesta cliente Finup 04/06/2026): usiamo `deal.amount` come fonte
      // di verità per i ricavi mensili. Il prezzo dell'offerta viene modificato/scontato
      // manualmente nel campo dedicato del deal; la somma servizi può divergere.
      // Se FIC attivo: usiamo net_amount delle fatture emesse nel mese al netto NDC.
      let monthlyRevenue: number;
      if (useFic) {
        const monthFic = filterFicByUsers(ficInvoices.filter(inv => inv.date >= monthStart && inv.date < monthEnd), scope);
        monthlyRevenue = calcFicRevenue(monthFic);
      } else {
        monthlyRevenue = closedDeals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0);
      }

      const totalDeals = monthDeals?.length || 0;
      const conversionRate = totalDeals > 0 ? (closedDeals.length / totalDeals) * 100 : 0;
      const avgDealSize = closedDeals.length > 0 ? monthlyRevenue / closedDeals.length : 0;

      // Calculate conversion rate for deals created AND closed in the same month
      const createdAndClosedThisMonth = filteredDeals?.filter(d => {
        const createdDate = d.created_at;
        const signedDate = d.signed_at;
        const isCreatedThisMonth = createdDate && createdDate >= monthStart && createdDate < monthEnd;
        const isSignedThisMonth = signedDate && signedDate >= monthStart && signedDate < monthEnd;
        return isCreatedThisMonth && d.status === 'accepted' && isSignedThisMonth;
      }) || [];

      const createdThisMonth = filteredDeals?.filter(d => {
        const createdDate = d.created_at;
        return createdDate && createdDate >= monthStart && createdDate < monthEnd;
      }) || [];

      const conversionRateMonthly = createdThisMonth.length > 0
        ? (createdAndClosedThisMonth.length / createdThisMonth.length) * 100
        : 0;

      // Calculate conversion rate for deals created AND closed in the same year
      // currentYear is already available from getMonthRange above
      const yearStart = new Date(currentYear, 0, 1).toISOString();
      const yearEnd = new Date(currentYear + 1, 0, 1).toISOString();

      const createdAndClosedThisYear = filteredDeals?.filter(d => {
        const createdDate = d.created_at;
        const signedDate = d.signed_at;
        const isCreatedThisYear = createdDate && createdDate >= yearStart && createdDate < yearEnd;
        const isSignedThisYear = signedDate && signedDate >= yearStart && signedDate < yearEnd;
        return isCreatedThisYear && d.status === 'accepted' && isSignedThisYear;
      }) || [];

      const createdThisYear = filteredDeals?.filter(d => {
        const createdDate = d.created_at;
        return createdDate && createdDate >= yearStart && createdDate < yearEnd;
      }) || [];

      const conversionRateYearly = createdThisYear.length > 0
        ? (createdAndClosedThisYear.length / createdThisYear.length) * 100
        : 0;

      // Create funnel data - Allineati con gli stati delle trattative nella sezione deals
      const stages = ['draft', 'sent', 'accepted', 'rejected'];
      const funnel: SalesFunnel[] = [];
      
      // Count deals in each stage and calculate value
      const stageCounts = new Map<string, number>();
      const stageValues = new Map<string, number>();
      
      // Initialize with zeros
      stages.forEach(stage => {
        stageCounts.set(stage, 0);
        stageValues.set(stage, 0);
      });
      
      // Count and sum
      monthDeals.forEach(deal => {
        const stage = deal.status;
        if (stages.includes(stage)) {
          stageCounts.set(stage, (stageCounts.get(stage) || 0) + 1);

          // R18a (Finup 04/06/2026): deal.amount è la fonte di verità per il valore
          // del deal nel funnel, coerente con `monthlyRevenue` riga 620. Lo sconto
          // manuale applicato al deal.amount NON si riflette sui servizi.
          const dealValue = Number(deal.amount || 0);

          stageValues.set(stage, (stageValues.get(stage) || 0) + dealValue);
        }
      });
      
      // Calculate conversion rates
      stages.forEach((stage, index) => {
        const count = stageCounts.get(stage) || 0;
        const value = stageValues.get(stage) || 0;

        // Calculate conversion rate from previous stage
        let conversionRate = 0;
        if (index > 0) {
          const previousStage = stages[index - 1];
          const previousCount = stageCounts.get(previousStage) || 0;

          if (previousCount > 0) {
            conversionRate = (count / previousCount) * 100;
          }
        }

        funnel.push({
          stage,
          count,
          value,
          conversion_rate: conversionRate,
          previous_stage: index > 0 ? stages[index - 1] : undefined
        });
      });

      // Debug: Log funnel data to verify all stages are present
      console.log('[SalesTargetStore] Funnel data:', funnel.map(f => ({ stage: f.stage, count: f.count, value: f.value })));
      
      // Create targets map
      const targetsMap = new Map();
      if (targets) {
        targets.forEach(target => {
          targetsMap.set(target.user_id, target.target_amount);
        });
      }

      // ── FIC: pre-compute revenue maps by user for performers ──
      let ficRevenueByUser: Map<string, number> | null = null;
      let ficPreviousRevenueByUser: Map<string, number> | null = null;
      if (useFic) {
        ficRevenueByUser = new Map();
        ficPreviousRevenueByUser = new Map();
        const periodFic = ficInvoices.filter(inv => inv.date >= performersStartDate && inv.date < performersEndDate);
        const prevFic = ficInvoices.filter(inv => inv.date >= previousStartDate && inv.date < previousEndDate);
        periodFic.forEach(inv => {
          const userId = inv.deals?.assigned_to || '__unassigned__';
          const amt = Number(inv.net_amount || 0) * (inv.type === 'credit_note' ? -1 : 1);
          ficRevenueByUser!.set(userId, (ficRevenueByUser!.get(userId) || 0) + amt);
        });
        prevFic.forEach(inv => {
          const userId = inv.deals?.assigned_to || '__unassigned__';
          const amt = Number(inv.net_amount || 0) * (inv.type === 'credit_note' ? -1 : 1);
          ficPreviousRevenueByUser!.set(userId, (ficPreviousRevenueByUser!.get(userId) || 0) + amt);
        });
      }

      // Create previous period revenue map for performance comparison
      const previousRevenueMap = new Map<string, number>();
      (team || []).forEach(member => {
        if (useFic && ficPreviousRevenueByUser) {
          previousRevenueMap.set(member.id, ficPreviousRevenueByUser.get(member.id) || 0);
          return;
        }

        const memberPreviousDeals = previousPeriodDeals
          ?.filter(d => d.assigned_to === member.id && d.status === 'accepted') || [];

        // R18a: previousRevenue da deal.amount (coerente con monthlyRevenue periodo corrente).
        const previousRevenue = memberPreviousDeals.reduce(
          (sum, deal) => sum + Number(deal.amount || 0),
          0,
        );

        previousRevenueMap.set(member.id, previousRevenue);
      });

      // Create performers data - tutte le offerte del team, ma con una persona scelta solo quella persona
      const performers: SalesPerformer[] = (team || []).filter(member => inScope(scope, member.id)).map(member => {
        // Find deals assigned to this team member, filtered for the selected month (from ALL deals)
        const memberMonthDeals = allMonthDeals?.filter(d => d.assigned_to === member.id) || [];
        // We filter accepted deals from the month-filtered deals
        const memberClosedDeals = memberMonthDeals.filter(d => d.status === 'accepted') || [];
        
        // R18a: revenue performer — da FIC se attivo, altrimenti deal.amount.
        const revenue = (useFic && ficRevenueByUser)
          ? (ficRevenueByUser.get(member.id) || 0)
          : memberClosedDeals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0);

        // Get target amount (defaults to 0 if not set)
        const targetAmount = targetsMap.get(member.id) || 0;

        // Calculate achievement percentage based on target
        let achievement = 0;
        if (targetAmount > 0) {
          achievement = (revenue / targetAmount) * 100;
        }
        // If no target is set, achievement remains 0

        // Calculate average deal size
        const avgDealSize = memberClosedDeals.length > 0 ? revenue / memberClosedDeals.length : 0;
        
        // Calculate conversion rate (based on month deals only)
        // Conversion rate should be based on all deals from this month
        const conversionRate = memberMonthDeals.length > 0 
          ? (memberClosedDeals.length / memberMonthDeals.length) * 100 
          : 0;
        
        // Find highest deal
        const highestDeal = memberClosedDeals.length > 0
          ? Math.max(...memberClosedDeals.map(d => d.amount || 0))
          : 0;
        
        return {
          user_id: member.id,
          user_name: member.full_name || member.email.split('@')[0],
          total_revenue: revenue,
          total_deals: memberClosedDeals.length,
          target_amount: targetAmount,
          achievement_percentage: achievement,
          avg_deal_size: avgDealSize,
          conversion_rate: conversionRate,
          highest_deal: highestDeal,
          avatar: member.avatar_url
        };
      });
      
      // Filter out admin users from performers
      const filteredPerformers = performers.filter(performer => {
        const user = team.find(u => u.id === performer.user_id);
        return user && !user.email.includes('admin');
      });
      
      // Sort performers by revenue and add ranks
      filteredPerformers.sort((a, b) => b.total_revenue - a.total_revenue);
      filteredPerformers.forEach((performer, index) => {
        performer.rank = index + 1;
      });
      
      // Process activities
      const activities: SalesActivity[] = (activitiesData || []).map(item => {
        // Resolve best contact with fallback chain for phone/email
        const dealContacts = item.deals?.deal_contacts || [];
        const dealPrimary = dealContacts.length > 0 && dealContacts[0].contact ? dealContacts[0].contact : null;
        const clientContacts: any[] =
          item.clients?.client_contacts ||
          item.leads?.client_contacts ||
          item.deals?.clients?.client_contacts ||
          [];
        let contact_info = resolveContactInfo(dealPrimary, dealPrimary ? 'deal' : 'client', clientContacts);

        // Fallback finale: phone/email diretto dal record clients/leads (incluso whatsapp_number)
        const clientDirect = item.clients || item.leads || item.deals?.clients;
        const directPhone = clientDirect?.phone || clientDirect?.whatsapp_number;
        if (contact_info) {
          if (!contact_info.phone && directPhone) contact_info.phone = directPhone;
          if (!contact_info.email && clientDirect?.email) contact_info.email = clientDirect.email;
        } else if (directPhone || clientDirect?.email) {
          const client_name_resolved = item.clients?.business_name || item.leads?.business_name || item.deals?.clients?.business_name;
          contact_info = {
            id: clientDirect.id || '',
            name: client_name_resolved || '',
            phone: directPhone || undefined,
            email: clientDirect.email || undefined,
            source: 'client',
          };
        }

        return {
          id: item.id,
          title: item.title,
          description: item.description,
          type: item.type,
          status: item.status,
          due_date: item.due_date,
          created_at: item.created_at,
          client_id: item.client_id || item.deals?.client_id,
          client_name: item.clients?.business_name
            || item.leads?.business_name
            || item.deals?.clients?.business_name
            || (item.partners?.name ? `Partner: ${item.partners.name}` : undefined),
          lead_id: item.lead_id,
          partner_id: item.partner_id,
          deal_id: item.deal_id,
          deal_title: item.deals?.title,
          deal_status: item.deals?.status,
          deal_amount: item.deals?.amount,
          user_id: item.user_id,
          assigned_to: item.assigned_to,
          assignee: item.assignee,
          completed_at: item.completed_at,
          outcome: item.outcome,
          contact_info,
        };
      });
      
      // Generate team performance data (last 6 months)
      // Use filtered deals when a user filter is active, otherwise all deals
      const dealsSourceForPerformance = scope.length > 0 ? filteredDeals : allDealsForPerformers;
      const performanceData: TeamPerformance[] = [];
      // Ancorata al mese scelto nella barra (non a oggi): grafico, variazione e "da gennaio" seguono il periodo
      // Almeno 6 mesi, e sempre da gennaio: "Ricavi annuali" somma i mesi dell'anno fino al mese scelto (21/10/2026)
      const monthsToShow = Math.max(6, currentMonth);

      for (let i = monthsToShow - 1; i >= 0; i--) {
        const date = new Date(currentYear, currentMonth - 1 - i, 1);
        const month = date.getMonth();
        const year = date.getFullYear();

        const monthFormatted = `${MONTH_NAMES[month]} ${year.toString().slice(2)}`;
        const monthIndex = month + 1; // Convert 0-based to 1-based
        const { monthStart: monthStartDate, monthEnd: monthEndDate } = getMonthRange(monthIndex, year);

        // Filter deals signed in this month
        const monthDeals = dealsSourceForPerformance?.filter(deal => {
          const isInMonth = deal.signed_at && deal.signed_at >= monthStartDate && deal.signed_at < monthEndDate;
          return isInMonth && deal.status === 'accepted';
        }) || [];

        // Filter out admin users
        // Le offerte senza commerciale assegnato contano nei ricavi (come in "Ricavi periodo")
        const nonAdminDeals = monthDeals.filter(deal => {
          const isIncluded = !deal.user_profiles || !deal.user_profiles.email.includes('admin');
          return isIncluded;
        });

        // Count deals CREATED in this month (for conversion rate calculation)
        const dealsCreatedThisMonth = dealsSourceForPerformance?.filter(deal => {
          const isCreatedInMonth = deal.created_at && deal.created_at >= monthStartDate && deal.created_at < monthEndDate;
          const isNotAdmin = !deal.user_profiles || !deal.user_profiles.email.includes('admin');
          return isCreatedInMonth && isNotAdmin;
        }) || [];

        // Count deals created AND closed in the same month
        const dealsCreatedAndClosedThisMonth = dealsCreatedThisMonth.filter(deal => {
          const isClosedInMonth = deal.signed_at && deal.signed_at >= monthStartDate && deal.signed_at < monthEndDate;
          return deal.status === 'accepted' && isClosedInMonth;
        });

        // Calculate conversion rate for this month
        const monthConversionRate = dealsCreatedThisMonth.length > 0
          ? (dealsCreatedAndClosedThisMonth.length / dealsCreatedThisMonth.length) * 100
          : 0;

        // R18a: monthRevenue — da FIC se attivo, altrimenti deal.amount.
        let monthRevenue: number;
        if (useFic) {
          const mFic = filterFicByUsers(ficInvoices.filter(inv => inv.date >= monthStartDate && inv.date < monthEndDate), scope);
          monthRevenue = calcFicRevenue(mFic);
        } else {
          monthRevenue = nonAdminDeals.reduce(
            (sum, deal) => sum + Number(deal.amount || 0),
            0,
          );
        }

        performanceData.push({
          month: monthFormatted,
          deals_closed: nonAdminDeals.length,
          deals_created: dealsCreatedThisMonth.length,
          conversion_rate: Math.round(monthConversionRate * 10) / 10,
          revenue: monthRevenue,
          month_number: month + 1,
          year
        });
      }
      
      // Update all data in the store at once
      setIf({
        monthlyRevenue,
        conversionRate,
        conversionRateMonthly,
        conversionRateYearly,
        avgDealSize,
        totalActivities: activities.length,
        performers: filteredPerformers,
        // La classifica sul mese usa gli stessi dati; sull'Anno la ricarica fetchPerformersOnly (qui sotto)
        performersPodiumData: performersPeriod === 'monthly' ? filteredPerformers : get().performersPodiumData,
        funnel,
        activities,
        teamPerformance: performanceData,
        statsLoading: false,
        performersLoading: false,
        funnelLoading: false,
        activitiesLoading: false,
        teamLoading: false
      });

      if (performersPeriod === 'yearly' && isLatest()) {
        void get().fetchPerformersOnly(user, filterUserId);
      }

      return {
        stats: { monthlyRevenue, conversionRate, conversionRateMonthly, conversionRateYearly, avgDealSize },
        performers: filteredPerformers,
        funnel,
        activities,
        teamPerformance: performanceData
      };
      
    } catch (error) {
      // Handle error silently and continue
      setIf({ 
        error: friendlyDbError(error),
        statsLoading: false,
        performersLoading: false,
        funnelLoading: false,
        activitiesLoading: false,
        teamLoading: false
      });
    }
  },

  // Fetch only performers data based on performersPeriodType
  fetchPerformersOnly: async (user, filterUserId = null) => {
    if (!user) return;

    set({ performersPodiumLoading: true });

    try {
      const organizationId = await get().getOrganizationId(user);
      const scope = scopeIds(filterUserId, get().selectedUserIds);
      const currentMonth = get().currentDashboardMonth;
      const currentYear = get().currentDashboardYear;
      const performersPeriod = get().performersPeriodType;

      // Get month range
      const { monthStart, monthEnd } = getMonthRange(currentMonth, currentYear);

      // Calculate date ranges for performers
      let performersStartDate: string, performersEndDate: string;
      let previousStartDate: string, previousEndDate: string;

      if (performersPeriod === 'yearly') {
        performersStartDate = `${currentYear}-01-01`;
        performersEndDate = `${currentYear + 1}-01-01`;
        previousStartDate = `${currentYear - 1}-01-01`;
        previousEndDate = `${currentYear}-01-01`;
      } else {
        performersStartDate = monthStart;
        performersEndDate = monthEnd;
        const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        const prevMonthRange = getMonthRange(prevMonth, prevYear);
        previousStartDate = prevMonthRange.monthStart;
        previousEndDate = prevMonthRange.monthEnd;
      }

      // Fetch ALL deals (no date filter in query)
      // A pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id
      const allDealsQuery = fetchAllPages((from, to) => supabase
        .from('deals')
        .select(`
          *,
          deal_services (
            id,
            service_id,
            amount,
            discounted_amount,
            monthly_duration
          )
        `)
        .eq('organization_id', organizationId)
        .order('id')
        .range(from, to));

      // Fetch team members and targets
      const [
        { data: allDeals, error: allDealsError },
        { data: team, error: teamError },
        { data: targets, error: targetsError }
      ] = await Promise.all([
        allDealsQuery,
        supabase
          .from('user_profiles')
          .select('id, email, full_name, avatar_url')
          .eq('organization_id', organizationId),
        // Obiettivi del periodo: per il Mese quelli del mese scelto, per l'Anno la tabella degli obiettivi annuali
        performersPeriod === 'yearly'
          ? supabase
              .from('sales_targets_yearly')
              .select('user_id, target_amount')
              .eq('organization_id', organizationId)
              .eq('year', currentYear)
          : supabase
              .from('sales_targets')
              .select('user_id, target_amount')
              .eq('organization_id', organizationId)
              .eq('year', currentYear)
              .eq('month', currentMonth)
      ]);

      if (allDealsError) throw allDealsError;
      if (teamError) throw teamError;

      // ── FIC override for performers-only refresh ──
      const useFicPerf = await shouldUseFicRevenue(organizationId);
      let ficRevByUser: Map<string, number> | null = null;
      let ficPrevRevByUser: Map<string, number> | null = null;
      if (useFicPerf) {
        const ficStart = previousStartDate < performersStartDate ? previousStartDate : performersStartDate;
        const ficEnd = performersEndDate;
        const ficRows = await fetchFicInvoices(organizationId, ficStart, ficEnd);
        ficRevByUser = new Map();
        ficPrevRevByUser = new Map();
        ficRows.forEach(inv => {
          const userId = inv.deals?.assigned_to || '__unassigned__';
          const amt = Number(inv.net_amount || 0) * (inv.type === 'credit_note' ? -1 : 1);
          if (inv.date >= performersStartDate && inv.date < performersEndDate) {
            ficRevByUser!.set(userId, (ficRevByUser!.get(userId) || 0) + amt);
          }
          if (inv.date >= previousStartDate && inv.date < previousEndDate) {
            ficPrevRevByUser!.set(userId, (ficPrevRevByUser!.get(userId) || 0) + amt);
          }
        });
      }

      // Filter deals for current and previous periods
      const currentPeriodDeals = allDeals?.filter(d => {
        const dealDate = d.signed_at;
        return dealDate && dealDate >= performersStartDate && dealDate < performersEndDate;
      }) || [];

      const previousPeriodDeals = allDeals?.filter(d => {
        const dealDate = d.signed_at;
        return dealDate && dealDate >= previousStartDate && dealDate < previousEndDate;
      }) || [];

      // Create targets map
      const targetsMap = new Map();
      if (targets) {
        targets.forEach(target => {
          targetsMap.set(target.user_id, target.target_amount);
        });
      }

      // Create previous period revenue map (stessa formula della classifica: deal.amount)
      const previousRevenueMap = new Map<string, number>();
      (team || []).forEach(member => {
        if (useFicPerf && ficPrevRevByUser) {
          previousRevenueMap.set(member.id, ficPrevRevByUser.get(member.id) || 0);
          return;
        }

        const previousRevenue = previousPeriodDeals
          .filter(d => d.assigned_to === member.id && d.status === 'accepted')
          .reduce((sum, deal) => sum + Number(deal.amount || 0), 0);

        previousRevenueMap.set(member.id, previousRevenue);
      });

      // Calculate performers (con una persona scelta solo quella persona, come gli altri blocchi)
      const performers: SalesPerformer[] = (team || []).filter(member => inScope(scope, member.id)).map(member => {
        const memberCurrentDeals = currentPeriodDeals?.filter(d => d.assigned_to === member.id) || [];
        const memberClosedDeals = memberCurrentDeals.filter(d => d.status === 'accepted') || [];

        let revenue: number;
        if (useFicPerf && ficRevByUser) {
          revenue = ficRevByUser.get(member.id) || 0;
        } else {
          // Stessa formula di fetchDashboardData: deal.amount (il prezzo dell'offerta, con lo sconto manuale)
          revenue = memberClosedDeals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0);
        }

        const targetAmount = targetsMap.get(member.id) || 0;

        // Calculate achievement percentage based on target
        let achievement = 0;
        if (targetAmount > 0) {
          achievement = (revenue / targetAmount) * 100;
        }
        // If no target is set, achievement remains 0

        const avgDealSize = memberClosedDeals.length > 0 ? revenue / memberClosedDeals.length : 0;
        const conversionRate = memberCurrentDeals.length > 0
          ? (memberClosedDeals.length / memberCurrentDeals.length) * 100
          : 0;
        const highestDeal = memberClosedDeals.length > 0
          ? Math.max(...memberClosedDeals.map(d => d.amount || 0))
          : 0;

        return {
          user_id: member.id,
          user_name: member.full_name || member.email.split('@')[0],
          total_revenue: revenue,
          total_deals: memberClosedDeals.length,
          target_amount: targetAmount,
          achievement_percentage: achievement,
          avg_deal_size: avgDealSize,
          conversion_rate: conversionRate,
          highest_deal: highestDeal,
          avatar: member.avatar_url
        };
      });

      // Filter out admin users
      const filteredPerformers = performers.filter(performer => {
        const user = team.find(u => u.id === performer.user_id);
        return user && !user.email.includes('admin');
      });

      // Sort and add ranks
      filteredPerformers.sort((a, b) => b.total_revenue - a.total_revenue);
      filteredPerformers.forEach((performer, index) => {
        performer.rank = index + 1;
      });

      set({
        performersPodiumData: filteredPerformers,
        performersPodiumLoading: false
      });

    } catch (error) {
      console.error('[SalesTargetStore] Error fetching performers:', error);
      set({ performersPodiumLoading: false, error: friendlyDbError(error) });
    }
  },

  // Action: Fetch sales targets
  fetchTargets: async (month?: number, year?: number) => {
    set({ loading: true, error: null });
    
    try {
      // Use current month/year if not specified
      const now = new Date();
      const currentMonth = month || now.getMonth() + 1;
      const currentYear = year || now.getFullYear();
      
      // Get user to determine organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');
      
      // Get user's organization ID
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }
      
      const organizationId = profile.organization_id;
      
      // Get targets for the organization, month, and year
      const { data, error } = await supabase
        .from('sales_targets')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .order('target_amount', { ascending: false });
        
      if (error) throw error;
      
      set({ targets: data || [], loading: false });
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), loading: false });
    }
  },
  
  // Action: Create new sales target
  createTarget: async (target: any) => {
    try {
      const { error } = await supabase
        .from('sales_targets')
        .insert(target);
        
      if (error) throw error;
      
      return true;
    } catch (error) {
      // Handle error and rethrow
      throw error;
    }
  },
  
  // Action: Update sales target
  updateTarget: async (id: string, target: any) => {
    try {
      const { error } = await supabase
        .from('sales_targets')
        .update(target)
        .eq('id', id);
        
      if (error) throw error;
      
      return true;
    } catch (error) {
      // Handle error and rethrow
      throw error;
    }
  },
  
  // Action: Delete sales target
  deleteTarget: async (id: string) => {
    try {
      const { error } = await supabase
        .from('sales_targets')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      return true;
    } catch (error) {
      // Handle error and rethrow
      throw error;
    }
  },
  
  // Action: Fetch key performance stats
  fetchDashboardStats: async (user, month, year, filterUserId = null) => {
    if (!user) return;
    
    set({ statsLoading: true, error: null });
    
    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);
      
      // Get month range using our utility function
      const { monthStart, monthEnd } = getMonthRange(month || 0, year || 0);
      
      // Build the query
      let query = supabase
        .from('deals')
        .select('id, amount, status, created_at, signed_at, assigned_to')
        .eq('organization_id', organizationId)
        .gte('signed_at', monthStart)
        .lt('signed_at', monthEnd);
      
      // Apply user filter if specified
      if (filterUserId) {
        query = query.eq('assigned_to', filterUserId);
      }
      
      // Execute the query
      const { data: deals, error: dealsError } = await query;
      
      if (dealsError) throw dealsError;
      
      // Calculate monthly revenue from closed deals
      const closedDeals = deals?.filter(d => d.status === 'accepted') || [];
      const monthlyRevenue = closedDeals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
      
      // Calculate conversion rate (closed deals / total deals)
      const totalDeals = deals?.length || 0;
      const conversionRate = totalDeals > 0 ? (closedDeals.length / totalDeals) * 100 : 0;
      
      // Calculate average deal size
      const avgDealSize = closedDeals.length > 0 
        ? monthlyRevenue / closedDeals.length 
        : 0;
      
      // Skip activities count if table doesn't exist
      // We'll handle this gracefully instead of throwing errors
      let activitiesCount = 0;
      try {
        // Try to get total activities, but don't fail if table doesn't exist
        const { count, error } = await supabase
          .from('sales_activities')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .gte('created_at', monthStart)
          .lt('created_at', monthEnd);
          
        if (!error) {
          activitiesCount = count || 0;
        }
      } catch (activityError) {
        // Continue without activities data
      }
      
      // Update the store
      set({
        monthlyRevenue,
        conversionRate,
        avgDealSize,
        totalActivities: activitiesCount || 0,
        statsLoading: false
      });
      
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), statsLoading: false });
    }
  },
  
  // Action: Fetch sales performers leaderboard
  fetchPerformers: async (user, month, year) => {
    if (!user) return;
    
    set({ performersLoading: true, error: null });
    
    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);
      
      // Get month range using our utility function
      const { monthStart, monthEnd, month: currentMonth, year: currentYear } = getMonthRange(month || 0, year || 0);
      
      // Get sales team members
      const { data: team, error: teamError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, avatar_url')
        .eq('organization_id', organizationId);
        
      if (teamError) throw teamError;
      
      if (!team || team.length === 0) {
        set({ performers: [], performersLoading: false });
        return;
      }
      
      // Get sales targets for each team member - handle missing table gracefully
      const targetsMap = new Map();
      try {
        const { data: targets, error: targetsError } = await supabase
          .from('sales_targets')
          .select('user_id, target_amount')
          .eq('organization_id', organizationId)
          .eq('month', currentMonth)
          .eq('year', currentYear);
          
        // Not throwing errors for targets - some users might not have targets
        if (!targetsError && targets) {
          targets.forEach(target => {
            targetsMap.set(target.user_id, target.target_amount);
          });
        }
      } catch (targetError) {
        // Continue without targets data
      }
      
      // Otteniamo tutti i deal e i loro servizi
      const { data: deals, error: dealsError } = await supabase
        .from('deals')
        .select(`
          id,
          amount,
          status,
          assigned_to,
          created_at,
          signed_at,
          title,
          deal_services(id, amount, monthly_duration, discounted_amount)
        `)
        .eq('organization_id', organizationId)
        .eq('status', 'accepted');  // Solo deal accettati
        
      if (dealsError) {
        throw dealsError;
      }
      
      // Otteniamo tutti i deal per l'organizzazione nel periodo specificato
      const { data: allDeals, error: allDealsError } = await supabase
        .from('deals')
        .select(`
          id,
          amount,
          status,
          assigned_to,
          created_at,
          signed_at,
          title
        `)
        .eq('organization_id', organizationId)
        .gte('signed_at', monthStart)
        .lt('signed_at', monthEnd);
        
      if (allDealsError) {
        throw allDealsError;
      }
      
      // Calculate performance for each team member
      const performers: SalesPerformer[] = team.map(member => {
        // Find deals assigned to this team member (dai deal già chiusi e accettati)
        const memberClosedDeals = deals?.filter(d => d.assigned_to === member.id) || [];
        
        // R18a: revenue leaderboard performer da deal.amount (coerente con fetchDashboardStats).
        // `serviceDetails` precedente era debug array popolato ma mai usato nel return → rimosso.
        const revenue = memberClosedDeals.reduce(
          (sum, deal) => sum + Number(deal.amount || 0),
          0,
        );
        
        // Get target amount (defaults to 0 if not set)
        const targetAmount = targetsMap.get(member.id) || 0;
        
        // Calculate achievement percentage
        const achievement = targetAmount > 0 ? (revenue / targetAmount) * 100 : 0;
        
        // Calculate average deal size
        const avgDealSize = memberClosedDeals.length > 0 ? revenue / memberClosedDeals.length : 0;
        
        // Calcolo del conversion rate usando tutti i deal dell'utente
        const allMemberDeals = allDeals?.filter(d => d.assigned_to === member.id) || [];
        const conversionRate = allMemberDeals.length > 0 
          ? (memberClosedDeals.length / allMemberDeals.length) * 100 
          : 0;
        
        // Find highest deal
        const highestDeal = memberClosedDeals.length > 0
          ? Math.max(...memberClosedDeals.map(d => d.amount || 0))
          : 0;
        
        return {
          user_id: member.id,
          user_name: member.full_name || member.email.split('@')[0],
          total_revenue: revenue,
          total_deals: memberClosedDeals.length,
          target_amount: targetAmount,
          achievement_percentage: achievement,
          avg_deal_size: avgDealSize,
          conversion_rate: conversionRate,
          highest_deal: highestDeal,
          avatar: member.avatar_url
        };
      });
      
      // Soluzione più semplice: filtriamo direttamente per email che contiene "admin"
      const filteredPerformers = performers.filter(performer => {
        // Get the user from the team array using the user_id
        const user = team.find(u => u.id === performer.user_id);
        // Check if the user email doesn't contain admin
        const isFiltered = user && !user.email.includes('admin');
        return isFiltered;
      });
      
      // First sort by total_revenue (descending) to prioritize actual sales
      filteredPerformers.sort((a, b) => b.total_revenue - a.total_revenue);
      
      // Then sort by achievement percentage (descending) if both have same revenue
      filteredPerformers.forEach((a, i) => {
        filteredPerformers.forEach((b, j) => {
          if (j > i && a.total_revenue === b.total_revenue && 
              a.achievement_percentage < b.achievement_percentage) {
            // Swap
            [filteredPerformers[i], filteredPerformers[j]] = 
            [filteredPerformers[j], filteredPerformers[i]];
          }
        });
      });
      
      // Add rank to each performer
      filteredPerformers.forEach((performer, index) => {
        performer.rank = index + 1;
      });
      
      set({ performers: filteredPerformers, performersLoading: false });
      
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), performersLoading: false });
    }
  },
  
  // Action: Fetch sales funnel data
  fetchSalesFunnel: async (user, month, year) => {
    if (!user) return;
    
    set({ funnelLoading: true, error: null });
    
    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);
      
      // Get month range using our utility function
      const { monthStart, monthEnd } = getMonthRange(month || 0, year || 0);
      
      // Define the funnel stages and their order - allineati con gli stati delle trattative della sezione deals
      const stages = ['draft', 'sent', 'accepted', 'rejected'];
      
      // Get deals for the specified month
      const { data: deals, error: dealsError } = await supabase
        .from('deals')
        .select('id, status, amount, signed_at')
        .eq('organization_id', organizationId)
        .gte('signed_at', monthStart)
        .lt('signed_at', monthEnd);
        
      if (dealsError) {
        // Error fetching deals
        set({ funnel: [], funnelLoading: false });
        return;
      }
      
      if (!deals || deals.length === 0) {
        set({ funnel: [], funnelLoading: false });
        return;
      }
      
      // Count deals in each stage and calculate value
      const stageCounts = new Map<string, number>();
      const stageValues = new Map<string, number>();
      
      // Initialize with zeros
      stages.forEach(stage => {
        stageCounts.set(stage, 0);
        stageValues.set(stage, 0);
      });
      
      // Count and sum
      deals.forEach(deal => {
        const stage = deal.status;
        if (stages.includes(stage)) {
          stageCounts.set(stage, (stageCounts.get(stage) || 0) + 1);
          stageValues.set(stage, (stageValues.get(stage) || 0) + (deal.amount || 0));
        }
      });
      
      // Calculate conversion rates
      const funnel: SalesFunnel[] = [];
      
      stages.forEach((stage, index) => {
        const count = stageCounts.get(stage) || 0;
        const value = stageValues.get(stage) || 0;
        
        // Calculate conversion rate from previous stage
        let conversionRate = 0;
        if (index > 0) {
          const previousStage = stages[index - 1];
          const previousCount = stageCounts.get(previousStage) || 0;
          
          if (previousCount > 0) {
            conversionRate = (count / previousCount) * 100;
          }
        }
        
        funnel.push({
          stage,
          count,
          value,
          conversion_rate: conversionRate,
          previous_stage: index > 0 ? stages[index - 1] : undefined
        });
      });
      
      set({ funnel, funnelLoading: false });
      
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), funnelLoading: false });
    }
  },
  
  // Action: Fetch sales activities
  fetchActivities: async (user, filterUserId, options) => {
    if (!user) return;

    if (options?.silent) {
      set({ error: null });
    } else {
      set({ activitiesLoading: true, error: null });
    }

    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);

      // Check if sales_activities table exists and get data if it does
      try {
        let activitiesQuery = supabase
          .from('sales_activities')
          .select(`
            id,
            title,
            description,
            type,
            status,
            due_date,
            created_at,
            client_id,
            clients:client_id (id, business_name, entity_type, stato_cliente, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
            lead_id,
            lead_client:lead_id (id, business_name, entity_type, stato_cliente, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
            partner_id,
            partners:external_partners!sales_activities_partner_id_fkey(id, name),
            deal_id,
            deals:deal_id (
              id,
              title,
              status,
              amount,
              client_id,
              clients:client_id(business_name, entity_type, stato_cliente, phone, email, whatsapp_number, client_contacts!client_contacts_client_id_fkey (id, name, phone, email, role, is_primary, is_referente)),
              deal_contacts (
                id,
                contact:client_contacts (
                  id,
                  name,
                  phone,
                  email,
                  role
                )
              ),
              deal_services (
                service_id,
                service:services (
                  id,
                  title
                )
              )
            ),
            user_id,
            assigned_to,
            assignee:assigned_to (id, full_name, email),
            creator:user_profiles!sales_activities_user_id_fkey1 (id, full_name, email),
            completed_at,
            outcome,
            end_date,
            google_event_id,
            calendar_id,
            microsoft_event_id,
            meet_link,
            metadata
          `)
          .eq('organization_id', organizationId)
          // Escludi le completate oltre 90gg: senza questo filtro PostgREST tronca a 1000 righe
          // e i task futuri (in coda per due_date asc) spariscono dalla dashboard
          .or(`status.neq.completed,completed_at.is.null,completed_at.gte.${new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()}`)
          .order('due_date', { ascending: true });

        // Apply filter by user for activities if provided
        // filterUserId = null means "all users", filterUserId = specific ID means filter to that user
        if (filterUserId !== null && filterUserId !== undefined) {
          // Filter to specific user only; con includeUnassignedCreated anche le sue senza assegnatario
          activitiesQuery = options?.includeUnassignedCreated
            ? activitiesQuery.or(assigneeScopeCondition(filterUserId))
            : activitiesQuery.eq('assigned_to', filterUserId);
        }
        // Piu' persone scelte nella barra: le task di tutte loro (come gli altri blocchi)
        const manyIds = (filterUserId === null || filterUserId === undefined) ? scopeIds(null, get().selectedUserIds) : [];
        if (manyIds.length > 1) activitiesQuery = activitiesQuery.in('assigned_to', manyIds);
        // If filterUserId is null/undefined, don't apply any user filter - show all activities

        // Filtro "Assegnate da me" / "Create da me": user_id = chi ha creato la task
        if (options?.createdBy) {
          activitiesQuery = activitiesQuery.eq('user_id', options.createdBy);
        }

        const { data, error: activitiesError } = await activitiesQuery;
          
        if (!activitiesError) {
          // Transform to our activity format
          const activities: SalesActivity[] = (data || []).map(item => {
            // Determine client name with priority: direct client -> direct lead -> deal client
            let client_name = item.clients?.business_name || item.lead_client?.business_name;
            // Use lead_id as client_id if no direct client_id (leads and clients use the same table/modal)
            let final_client_id = item.client_id || item.lead_id;

            // If no direct client/lead, try to get from associated deal
            if (!client_name && item.deals?.clients?.business_name) {
              client_name = item.deals.clients.business_name;
              final_client_id = item.deals.client_id;
            }

            // Attività di segnalatore/partner: nessun cliente/lead/deal collegato,
            // mostrare comunque un'etichetta invece di lasciare il nome vuoto
            if (!client_name && item.partners?.name) {
              client_name = `Partner: ${item.partners.name}`;
            }

            // Resolve best contact with fallback chain for phone/email
            const dealContacts = item.deals?.deal_contacts || [];
            const dealPrimary = dealContacts.length > 0 && dealContacts[0].contact ? dealContacts[0].contact : null;
            const clientContacts: any[] =
              item.clients?.client_contacts ||
              item.lead_client?.client_contacts ||
              item.deals?.clients?.client_contacts ||
              [];
            let contact_info = resolveContactInfo(dealPrimary, dealPrimary ? 'deal' : 'client', clientContacts);

            // Fallback finale: phone/email diretto dal record clients/leads (incluso whatsapp_number)
            const clientDirect = item.clients || item.lead_client || item.deals?.clients;
            const directPhone = clientDirect?.phone || clientDirect?.whatsapp_number;
            if (contact_info) {
              if (!contact_info.phone && directPhone) contact_info.phone = directPhone;
              if (!contact_info.email && clientDirect?.email) contact_info.email = clientDirect.email;
            } else if (directPhone || clientDirect?.email) {
              contact_info = {
                id: clientDirect.id || '',
                name: client_name || '',
                phone: directPhone || undefined,
                email: clientDirect.email || undefined,
                source: 'client',
              };
            }

            return {
              id: item.id,
              title: item.title,
              description: item.description,
              type: item.type,
              status: item.status,
              due_date: item.due_date,
              client_id: final_client_id, // Now correctly includes lead_id when client_id is null
              client_name: client_name,
              client_status: (item.clients || item.lead_client || item.deals?.clients)?.stato_cliente ?? null,
              lead_id: item.lead_id,
              partner_id: item.partner_id,
              deal_id: item.deal_id,
              deal_title: item.deals?.title,
              deal_status: item.deals?.status,
              deal_amount: item.deals?.amount,
              deal_contacts: item.deals?.deal_contacts || [],
              deal_services: item.deals?.deal_services || [],
              contact_info,
              user_id: item.user_id,
              assigned_to: item.assigned_to,
              assignee: item.assignee,
              creator: item.creator,
              completed_at: item.completed_at,
              outcome: item.outcome,
              // Riferimenti calendario: servono al drawer (modifica senza evento doppio)
              // e al "Rimanda" della scheda Task commerciali
              end_date: item.end_date,
              google_event_id: item.google_event_id,
              calendar_id: item.calendar_id,
              microsoft_event_id: item.microsoft_event_id,
              meet_link: item.meet_link,
              metadata: item.metadata
            };
          });

          set({ activities, activitiesLoading: false });
        } else {
          // Table doesn't exist or error - set empty activities
          set({ activities: [], activitiesLoading: false });
        }
      } catch (tableError) {
        // Return empty activities list
        set({ activities: [], activitiesLoading: false });
      }
      
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), activitiesLoading: false });
    }
  },
  
  // Action: Fetch team performance over time
  fetchTeamPerformance: async (user, months = 6) => {
    if (!user) return;
    
    set({ teamLoading: true, error: null });
    
    try {
      // Get user's organization ID using the cached function
      const organizationId = await get().getOrganizationId(user);
      
      // Generate month range
      const now = new Date();
      const performanceData: TeamPerformance[] = [];

      // Tutte le offerte, lette una volta sola e a pagine (PostgREST taglia a 1.000 righe), con ordine stabile per id
      // (prima la stessa query veniva rifatta identica a ogni mese del ciclo)
      // Recuperiamo i deal e i loro servizi, ma escludiamo quelli assegnati agli admin
      const { data: allDeals, error: allDealsError } = await fetchAllPages((from, to) => supabase
        .from('deals')
        .select(`
          id,
          amount,
          created_at,
          signed_at,
          status,
          updated_at,
          assigned_to,
          user_profiles!deals_assigned_to_fkey (
            email
          ),
          deal_services(id, amount, monthly_duration, discounted_amount)
        `)
        .eq('organization_id', organizationId)
        .order('id')
        .range(from, to));

      // Generate month range going back `months` months
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = date.getMonth();
        const year = date.getFullYear();
        
        // Format month for display
        const monthFormatted = `${MONTH_NAMES[month]} ${year.toString().slice(2)}`;
        
        // Get month range for this month
        const monthIndex = month + 1; // Convert 0-based to 1-based
        const { monthStart: monthStartDate, monthEnd: monthEndDate } = getMonthRange(monthIndex, year);
        
        // Get ALL deals for this month (for conversion rate calculation)

        // Filtriamo i deal escludendo quelli assegnati agli admin
        const filteredAllDeals = allDeals?.filter(deal => {
          const isIncluded = deal.user_profiles && !deal.user_profiles.email.includes('admin');
          return isIncluded;
        }) || [];

        // Filter accepted deals for revenue calculation
        const filteredDeals = filteredAllDeals.filter(deal => deal.status === 'accepted');

        if (allDealsError) {
          performanceData.push({
            month: monthFormatted,
            deals_closed: 0,
            deals_created: 0,
            conversion_rate: 0,
            revenue: 0,
            month_number: month + 1,
            year
          });
          continue; // Skip to next iteration instead of throwing
        }

        // Count deals CREATED in this month (for conversion rate)
        const dealsCreatedThisMonth = filteredAllDeals.filter(deal => {
          return deal.created_at && deal.created_at >= monthStartDate && deal.created_at < monthEndDate;
        });

        // Count deals created AND closed in the same month
        const dealsCreatedAndClosedThisMonth = dealsCreatedThisMonth.filter(deal => {
          const isClosedInMonth = deal.signed_at && deal.signed_at >= monthStartDate && deal.signed_at < monthEndDate;
          return deal.status === 'accepted' && isClosedInMonth;
        });

        // Calculate conversion rate for this month
        const monthConversionRate = dealsCreatedThisMonth.length > 0
          ? (dealsCreatedAndClosedThisMonth.length / dealsCreatedThisMonth.length) * 100
          : 0;
        
        // Filter deals that were signed in this month
        const monthDeals = filteredDeals?.filter(deal => {
          // Use signed_at for filtering
          const dealDate = deal.signed_at;
          const isInMonth = dealDate && dealDate >= monthStartDate && dealDate < monthEndDate;
          return isInMonth;
        }) || [];
        
        // Calculate totals for this month only using the filtered deals
        const dealsCount = monthDeals.length || 0;
        
        // R18a (richiesta cliente Finup 04/06/2026): il totale ricavi del mese
        // è la somma di `deal.amount`, non la somma dei deal_services. Il prezzo
        // di un'offerta viene modificato/scontato manualmente nel campo dedicato
        // del deal; la somma servizi può divergere (es. variabili escluse,
        // ricalcoli mancanti). Pre-fix moltiplicava anche per monthly_duration:
        // approccio ambiguo per ricavi storici, eliminato.
        const revenue = monthDeals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0);
        
        performanceData.push({
          month: monthFormatted,
          deals_closed: dealsCount,
          deals_created: dealsCreatedThisMonth.length,
          conversion_rate: Math.round(monthConversionRate * 10) / 10,
          revenue,
          month_number: month + 1,
          year
        });
      }
      
      set({ teamPerformance: performanceData, teamLoading: false });
      
    } catch (error) {
      // Handle error silently and continue
      set({ error: friendlyDbError(error), teamLoading: false });
    }
  },

  // ============================================================================
  // SALES ANALYTICS METHODS
  // ============================================================================

  // Fetch Service Categories Analysis
  fetchServiceCategoriesAnalysis: async (user, month, year, filterUserId = null, dateRange = null) => {
    // Vale solo l'ultima richiesta: la risposta di un filtro superato non scrive nello store
    const setIf = analyticsGate.guard(analyticsGate.begin('fetchServiceCategoriesAnalysis'), set);
    if (!user) return;

    setIf({ categoriesAnalysisLoading: true });

    try {
      const organizationId = await get().getOrganizationId(user);
      const scope = scopeIds(filterUserId, get().selectedUserIds);

      // Calculate date range
      let monthStart: string;
      let monthEnd: string;

      if (dateRange && dateRange.from && dateRange.to) {
        monthStart = dateRange.from;
        const toDate = new Date(dateRange.to);
        toDate.setDate(toDate.getDate() + 1);
        monthEnd = toDate.toISOString().split('T')[0];
      } else {
        const range = getMonthRange(month || 0, year || 0);
        monthStart = range.monthStart;
        monthEnd = range.monthEnd;
      }

      // ── FIC branch: aggregate invoices by category ──
      const useFicCat = await shouldUseFicRevenue(organizationId);
      if (useFicCat) {
        // Con un filtro persona solo le fatture di offerte assegnate a quelle persone (vedi filterFicByUsers)
        const ficRows = filterFicByUsers(await fetchFicInvoices(organizationId, monthStart, monthEnd), scope);
        const categoryMap: Record<string, ServiceCategoryAnalysis> = {};
        const dealsByCategory: Record<string, Set<string>> = {};
        let totalRevenue = 0;

        ficRows.forEach(inv => {
          const cat = inv.deal_services?.services?.service_categories;
          const catId = cat?.id || UNCATEGORIZED.id;
          const catName = cat?.name || UNCATEGORIZED.name;
          const catColor = cat?.color || null;
          const amt = Number(inv.net_amount || 0) * (inv.type === 'credit_note' ? -1 : 1);
          totalRevenue += amt;

          if (!categoryMap[catId]) {
            categoryMap[catId] = { categoryId: catId, categoryName: catName, categoryColor: catColor, totalRevenue: 0, dealsCount: 0, servicesCount: 0, percentageOfTotal: 0 };
            dealsByCategory[catId] = new Set();
          }
          categoryMap[catId].totalRevenue += amt;
          categoryMap[catId].servicesCount += 1;
          if (inv.deal_id) dealsByCategory[catId].add(inv.deal_id);
        });

        const categoriesAnalysis = Object.values(categoryMap).map(cat => ({
          ...cat,
          dealsCount: dealsByCategory[cat.categoryId].size,
          percentageOfTotal: totalRevenue > 0 ? (cat.totalRevenue / totalRevenue) * 100 : 0
        }));
        categoriesAnalysis.sort((a, b) => b.totalRevenue - a.totalRevenue);
        setIf({ serviceCategoriesAnalysis: categoriesAnalysis, categoriesAnalysisLoading: false });
        return;
      }

      // ── Default branch: deal_services ──
      // A pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id; filtro persona (una o piu') se presente
      const { data, error } = await fetchAllPages((from, to) => applyScope(supabase
        .from('deal_services')
        .select(`
          service_id,
          amount,
          discounted_amount,
          monthly_duration,
          services!inner (
            id,
            title,
            category_id,
            service_categories (
              id,
              name,
              color
            )
          ),
          deals!inner (
            id,
            status,
            signed_at,
            organization_id,
            assigned_to
          )
        `)
        .eq('deals.organization_id', organizationId)
        .eq('deals.status', 'accepted')
        .gte('deals.signed_at', monthStart)
        .lt('deals.signed_at', monthEnd), 'deals.assigned_to', scope)
        .order('id')
        .range(from, to));

      if (error) throw error;

      // Aggregate by category
      const categoryMap: Record<string, ServiceCategoryAnalysis> = {};
      const dealsByCategory: Record<string, Set<string>> = {};
      let totalRevenue = 0;

      data?.forEach((ds: any) => {
        // I servizi senza categoria finiscono in "Senza categoria" (prima venivano scartati e il totale non quadrava)
        const category = ds.services?.service_categories || UNCATEGORIZED;

        const categoryId = category.id;
        const revenue = (ds.discounted_amount ?? ds.amount) * (ds.monthly_duration || 1);
        totalRevenue += revenue;

        if (!categoryMap[categoryId]) {
          categoryMap[categoryId] = {
            categoryId,
            categoryName: category.name,
            categoryColor: category.color,
            totalRevenue: 0,
            dealsCount: 0,
            servicesCount: 0,
            percentageOfTotal: 0
          };
          dealsByCategory[categoryId] = new Set();
        }

        categoryMap[categoryId].totalRevenue += revenue;
        categoryMap[categoryId].servicesCount += 1;
        dealsByCategory[categoryId].add(ds.deals.id);
      });

      // Calculate percentages and deal counts
      const categoriesAnalysis = Object.values(categoryMap).map(cat => ({
        ...cat,
        dealsCount: dealsByCategory[cat.categoryId].size,
        percentageOfTotal: totalRevenue > 0 ? (cat.totalRevenue / totalRevenue) * 100 : 0
      }));

      // Sort by revenue descending
      categoriesAnalysis.sort((a, b) => b.totalRevenue - a.totalRevenue);

      setIf({ serviceCategoriesAnalysis: categoriesAnalysis, categoriesAnalysisLoading: false });

    } catch (error) {
      console.error('Error fetching service categories analysis:', error);
      setIf({ categoriesAnalysisLoading: false });
    }
  },

  // Fetch User Category Performance
  fetchUserCategoryPerformance: async (user, month, year, filterUserId = null, dateRange = null) => {
    // Vale solo l'ultima richiesta: la risposta di un filtro superato non scrive nello store
    const setIf = analyticsGate.guard(analyticsGate.begin('fetchUserCategoryPerformance'), set);
    if (!user) return;

    setIf({ userCategoryLoading: true });

    try {
      const organizationId = await get().getOrganizationId(user);
      const scope = scopeIds(filterUserId, get().selectedUserIds);

      // Calculate date range
      let monthStart: string;
      let monthEnd: string;

      if (dateRange && dateRange.from && dateRange.to) {
        monthStart = dateRange.from;
        const toDate = new Date(dateRange.to);
        toDate.setDate(toDate.getDate() + 1);
        monthEnd = toDate.toISOString().split('T')[0];
      } else {
        const range = getMonthRange(month || 0, year || 0);
        monthStart = range.monthStart;
        monthEnd = range.monthEnd;
      }

      // ── FIC branch: aggregate invoices by user + category ──
      const useFicUC = await shouldUseFicRevenue(organizationId);
      if (useFicUC) {
        // Con un filtro persona solo le fatture di offerte assegnate a quelle persone (vedi filterFicByUsers)
        const ficRows = filterFicByUsers(await fetchFicInvoices(organizationId, monthStart, monthEnd), scope);
        // Need user names — fetch team
        const { data: teamData } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .eq('organization_id', organizationId);
        const teamMap = new Map((teamData || []).map(u => [u.id, u.full_name || u.email || 'Unknown']));

        const userCategoryMatrix: Record<string, UserCategoryPerformance> = {};
        const dealsByUserCategory: Record<string, Record<string, Set<string>>> = {};

        ficRows.forEach(inv => {
          const userId = inv.deals?.assigned_to;
          if (!userId) return; // skip unassigned for per-user chart
          const cat = inv.deal_services?.services?.service_categories;
          const catId = cat?.id || UNCATEGORIZED.id;
          const catName = cat?.name || UNCATEGORIZED.name;
          const catColor = cat?.color || null;
          const amt = Number(inv.net_amount || 0) * (inv.type === 'credit_note' ? -1 : 1);

          if (!userCategoryMatrix[userId]) {
            userCategoryMatrix[userId] = { userId, userName: teamMap.get(userId) || 'Unknown', categories: {}, topCategory: null };
            dealsByUserCategory[userId] = {};
          }
          if (!userCategoryMatrix[userId].categories[catId]) {
            userCategoryMatrix[userId].categories[catId] = { categoryName: catName, categoryColor: catColor, revenue: 0, dealsCount: 0, percentageOfUserTotal: 0 };
            dealsByUserCategory[userId][catId] = new Set();
          }
          userCategoryMatrix[userId].categories[catId].revenue += amt;
          if (inv.deal_id) dealsByUserCategory[userId][catId].add(inv.deal_id);
        });

        const userCategoryPerformance = Object.values(userCategoryMatrix).map(u => {
          const total = Object.values(u.categories).reduce((s, c) => s + c.revenue, 0);
          Object.keys(u.categories).forEach(cId => {
            u.categories[cId].percentageOfUserTotal = total > 0 ? (u.categories[cId].revenue / total) * 100 : 0;
            u.categories[cId].dealsCount = dealsByUserCategory[u.userId][cId].size;
          });
          const cats = Object.entries(u.categories);
          if (cats.length > 0) {
            const top = cats.reduce((m, [id, c]) => c.revenue > m[1].revenue ? [id, c] : m);
            u.topCategory = { categoryId: top[0], categoryName: top[1].categoryName, revenue: top[1].revenue, specializationRate: top[1].percentageOfUserTotal };
          }
          return u;
        });
        userCategoryPerformance.sort((a, b) => {
          const aT = Object.values(a.categories).reduce((s, c) => s + c.revenue, 0);
          const bT = Object.values(b.categories).reduce((s, c) => s + c.revenue, 0);
          return bT - aT;
        });
        setIf({ userCategoryPerformance, userCategoryLoading: false });
        return;
      }

      // ── Default branch: deal_services ──
      // A pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id; filtro persona (una o piu') se presente
      const { data, error } = await fetchAllPages((from, to) => applyScope(supabase
        .from('deal_services')
        .select(`
          amount,
          discounted_amount,
          monthly_duration,
          services!inner (
            category_id,
            service_categories (
              id,
              name,
              color
            )
          ),
          deals!inner (
            id,
            assigned_to,
            status,
            signed_at,
            organization_id,
            user_profiles!deals_assigned_to_fkey (
              id,
              full_name,
              email
            )
          )
        `)
        .eq('deals.organization_id', organizationId)
        .eq('deals.status', 'accepted')
        .gte('deals.signed_at', monthStart)
        .lt('deals.signed_at', monthEnd), 'deals.assigned_to', scope)
        .order('id')
        .range(from, to));

      if (error) throw error;

      // Aggregate by user and category
      const userCategoryMatrix: Record<string, UserCategoryPerformance> = {};
      const dealsByUserCategory: Record<string, Record<string, Set<string>>> = {};

      data?.forEach((ds: any) => {
        const userId = ds.deals?.assigned_to;
        const userName = ds.deals?.user_profiles?.full_name || ds.deals?.user_profiles?.email || 'Unknown';
        // I servizi senza categoria finiscono in "Senza categoria" (prima venivano scartati)
        const category = ds.services?.service_categories || UNCATEGORIZED;

        if (!userId) return;

        const categoryId = category.id;
        const revenue = (ds.discounted_amount ?? ds.amount) * (ds.monthly_duration || 1);

        // Initialize user if not exists
        if (!userCategoryMatrix[userId]) {
          userCategoryMatrix[userId] = {
            userId,
            userName,
            categories: {},
            topCategory: null
          };
          dealsByUserCategory[userId] = {};
        }

        // Initialize category for user if not exists
        if (!userCategoryMatrix[userId].categories[categoryId]) {
          userCategoryMatrix[userId].categories[categoryId] = {
            categoryName: category.name,
            categoryColor: category.color,
            revenue: 0,
            dealsCount: 0,
            percentageOfUserTotal: 0
          };
          dealsByUserCategory[userId][categoryId] = new Set();
        }

        userCategoryMatrix[userId].categories[categoryId].revenue += revenue;
        dealsByUserCategory[userId][categoryId].add(ds.deals.id);
      });

      // Calculate percentages and top category for each user
      const userCategoryPerformance = Object.values(userCategoryMatrix).map(user => {
        const totalUserRevenue = Object.values(user.categories).reduce((sum, cat) => sum + cat.revenue, 0);

        // Update percentages and deal counts
        Object.keys(user.categories).forEach(catId => {
          user.categories[catId].percentageOfUserTotal =
            totalUserRevenue > 0 ? (user.categories[catId].revenue / totalUserRevenue) * 100 : 0;
          user.categories[catId].dealsCount = dealsByUserCategory[user.userId][catId].size;
        });

        // Find top category
        const categoriesArray = Object.entries(user.categories);
        if (categoriesArray.length > 0) {
          const topCat = categoriesArray.reduce((max, [catId, cat]) =>
            cat.revenue > max[1].revenue ? [catId, cat] : max
          );
          user.topCategory = {
            categoryId: topCat[0],
            categoryName: topCat[1].categoryName,
            revenue: topCat[1].revenue,
            specializationRate: topCat[1].percentageOfUserTotal
          };
        }

        return user;
      });

      // Sort by total revenue descending
      userCategoryPerformance.sort((a, b) => {
        const aTotal = Object.values(a.categories).reduce((sum, cat) => sum + cat.revenue, 0);
        const bTotal = Object.values(b.categories).reduce((sum, cat) => sum + cat.revenue, 0);
        return bTotal - aTotal;
      });

      setIf({ userCategoryPerformance, userCategoryLoading: false });

    } catch (error) {
      console.error('Error fetching user category performance:', error);
      setIf({ userCategoryLoading: false });
    }
  },

  // Fetch Service Type Trends
  fetchServiceTypeTrends: async (user, months = 6, filterUserId = null) => {
    // Vale solo l'ultima richiesta: la risposta di un filtro superato non scrive nello store
    const setIf = analyticsGate.guard(analyticsGate.begin('fetchServiceTypeTrends'), set);
    if (!user) return;

    setIf({ serviceTypeTrendsLoading: true });

    try {
      const organizationId = await get().getOrganizationId(user);

      // Finestra degli ultimi N mesi, con istanti completi: toISOString().split('T') da date locali perdeva un giorno
      const now = new Date();
      const { startDate, endDate } = trendWindow(now, months);

      const yearStart = startDate.toISOString();
      const yearEnd = endDate.toISOString();

      // Query deal_services with service type
      // A pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id; filtro persona (una o piu') se presente
      const scope = scopeIds(filterUserId, get().selectedUserIds);
      const { data, error } = await fetchAllPages((from, to) => applyScope(supabase
        .from('deal_services')
        .select(`
          amount,
          discounted_amount,
          monthly_duration,
          services!inner (
            service_type
          ),
          deals!inner (
            signed_at,
            status,
            organization_id,
            assigned_to
          )
        `)
        .eq('deals.organization_id', organizationId)
        .eq('deals.status', 'accepted')
        .gte('deals.signed_at', yearStart)
        .lt('deals.signed_at', yearEnd), 'deals.assigned_to', scope)
        .order('id')
        .range(from, to));

      if (error) throw error;

      // Somma per mese e tipo (ricorrente = monthly/annual); tutti i mesi della finestra, anche a zero
      const serviceTypeTrends: ServiceTypeRevenueTrend[] = aggregateServiceTrends(
        (data || []).map((ds: any) => ({
          signedAt: ds.deals.signed_at,
          serviceType: ds.services?.service_type,
          amount: ds.amount,
          discountedAmount: ds.discounted_amount,
          monthlyDuration: ds.monthly_duration,
        })),
        now,
        months
      );

      setIf({ serviceTypeTrends, serviceTypeTrendsLoading: false });

    } catch (error) {
      console.error('Error fetching service type trends:', error);
      setIf({ serviceTypeTrendsLoading: false });
    }
  },

  // Fetch Top Services Performance
  fetchTopServicesPerformance: async (user, month, year, limit = 10, filterUserId = null, dateRange = null) => {
    // Vale solo l'ultima richiesta: la risposta di un filtro superato non scrive nello store
    const setIf = analyticsGate.guard(analyticsGate.begin('fetchTopServicesPerformance'), set);
    if (!user) return;

    setIf({ topServicesLoading: true });

    try {
      const organizationId = await get().getOrganizationId(user);

      // Calculate date range
      let monthStart: string;
      let monthEnd: string;

      if (dateRange && dateRange.from && dateRange.to) {
        monthStart = dateRange.from;
        const toDate = new Date(dateRange.to);
        toDate.setDate(toDate.getDate() + 1);
        monthEnd = toDate.toISOString().split('T')[0];
      } else {
        const range = getMonthRange(month || 0, year || 0);
        monthStart = range.monthStart;
        monthEnd = range.monthEnd;
      }

      // Query deal_services (anche le righe senza servizio collegato: prodotti, nome personalizzato)
      // A pagine (PostgREST taglia a 1.000 righe) con ordine stabile per id; filtro persona (una o piu') se presente
      const scope = scopeIds(filterUserId, get().selectedUserIds);
      const { data, error } = await fetchAllPages((from, to) => applyScope(supabase
        .from('deal_services')
        .select(`
          service_id,
          custom_service_name,
          amount,
          discounted_amount,
          monthly_duration,
          services (
            id,
            title,
            list_price,
            service_type,
            service_categories (
              name,
              color
            )
          ),
          deals!inner (
            id,
            status,
            signed_at,
            organization_id,
            assigned_to
          )
        `)
        .eq('deals.organization_id', organizationId)
        .eq('deals.status', 'accepted')
        .gte('deals.signed_at', monthStart)
        .lt('deals.signed_at', monthEnd), 'deals.assigned_to', scope)
        .order('id')
        .range(from, to));

      if (error) throw error;

      // Aggregate by service (righe senza servizio: per nome personalizzato o in "Altre righe")
      const topServices: TopServicePerformance[] = aggregateTopServices(
        (data || []).map((ds: any) => ({
          dealId: ds.deals.id,
          serviceId: ds.service_id,
          serviceTitle: ds.services?.title,
          categoryName: ds.services?.service_categories?.name,
          categoryColor: ds.services?.service_categories?.color,
          serviceType: ds.services?.service_type,
          customServiceName: ds.custom_service_name,
          amount: ds.amount,
          discountedAmount: ds.discounted_amount,
          monthlyDuration: ds.monthly_duration,
        })),
        limit
      );

      setIf({ topServicesPerformance: topServices, topServicesLoading: false });

    } catch (error) {
      console.error('Error fetching top services performance:', error);
      setIf({ topServicesLoading: false });
    }
  },

  // Batch fetch all sales analytics
  fetchSalesAnalytics: async (user, month, year, filterUserId = null, dateRange = null) => {
    if (!user) return;

    try {
      // Il dettaglio dei grafici dei servizi (clic sui numeri) rifa' i conti con gli stessi periodo e persone
      rememberServicesScope({
        organizationId: await get().getOrganizationId(user),
        ...resolveServicesRange(month, year, dateRange),
        userIds: scopeIds(filterUserId, get().selectedUserIds),
        trendNow: Date.now(),
        trendMonths: 6,
      });
      // Fetch all analytics in parallel
      await Promise.all([
        get().fetchServiceCategoriesAnalysis(user, month, year, filterUserId, dateRange),
        get().fetchUserCategoryPerformance(user, month, year, filterUserId, dateRange),
        get().fetchServiceTypeTrends(user, 6, filterUserId),
        get().fetchTopServicesPerformance(user, month, year, 10, filterUserId, dateRange)
      ]);
    } catch (error) {
      console.error('Error fetching sales analytics:', error);
    }
  },

  // Reset store
  reset: () => {
    set({
      targets: [],
      performers: [],
      funnel: [],
      activities: [],
      teamPerformance: [],
      serviceCategoriesAnalysis: [],
      userCategoryPerformance: [],
      serviceTypeTrends: [],
      topServicesPerformance: [],
      monthlyRevenue: 0,
      conversionRate: 0,
      conversionRateMonthly: 0,
      conversionRateYearly: 0,
      avgDealSize: 0,
      totalActivities: 0,
      cachedOrganizationId: null,
      cachedUserId: null,
      selectedUserId: null,
      selectedUserIds: [],
      loading: false,
      statsLoading: false,
      performersLoading: false,
      funnelLoading: false,
      activitiesLoading: false,
      teamLoading: false,
      categoriesAnalysisLoading: false,
      userCategoryLoading: false,
      serviceTypeTrendsLoading: false,
      topServicesLoading: false,
      error: null
    });
  }
}));